import React from 'react';

/**
 * Shared "who completed/archived this" prettification (demo finding: "for
 * completed emails we're missing a lot of the details of who completed it
 * and when it was completed"). Used by TaskDetailPane and every lane's
 * completed/archived list rows so all of them render the AI marker the
 * same way instead of each re-inventing it.
 *
 * The CAF Backlog Processor writes two DIFFERENT literal marker strings
 * depending on which action it took -- `completed_by` on a live completion
 * (team_tasks.py's `AI_COMPLETED_BY = 'CAF Backlog Processor'`, spaced) vs
 * `archivedBy` on an auto-archive (caf_backlog_processor_service.py:
 * `"archivedBy": "CAF_BACKLOG_PROCESSOR"`, underscored). Both are kept
 * verbatim in the data (never rewritten server-side, never overridden by a
 * heuristic here) -- this module only controls what's *rendered*, mapping
 * either raw marker to the same human-readable "CAF AI processor" label
 * with a small badge, per the binding UI requirement.
 */

const AI_ACTOR_MARKERS = new Set(['CAF Backlog Processor', 'CAF_BACKLOG_PROCESSOR']);

export const AI_ACTOR_LABEL = 'CAF AI processor';

/** True when `raw` is one of the CAF Backlog Processor's known literal
 * markers (either spelling). */
export function isAiActor(raw: string | null | undefined): boolean {
  return !!raw && AI_ACTOR_MARKERS.has(raw);
}

/** Human-readable label for a completedBy/archivedBy value -- the AI
 * marker becomes `AI_ACTOR_LABEL`, everything else (a real staff email/
 * name) passes through unchanged. `null` when there's nothing to show. */
export function actorLabel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return isAiActor(raw) ? AI_ACTOR_LABEL : raw;
}

/** Renders a completedBy/archivedBy actor string, swapping the CAF Backlog
 * Processor's raw marker (either spelling, above) for a small "CAF AI
 * processor" badge instead of showing staff a literal string they'd have to
 * already know to recognise. The raw value is kept as the badge's `title`
 * tooltip -- prettified display only, the underlying data is untouched.
 * Always wraps `raw` in its own `<span>` (even in the plain-name case) so
 * it has a stable element boundary to hang a class on / query in tests,
 * rather than leaving a bare text node spliced into whatever surrounds it. */
export const ActorLabel: React.FC<{ raw: string }> = ({ raw }) =>
  isAiActor(raw) ? (
    <span className="lane-ai-actor-badge" title={raw}>{AI_ACTOR_LABEL}</span>
  ) : (
    <span className="lane-actor-name">{raw}</span>
  );
